import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { usePageScrollLock } from './usePageScrollLock';
import {
  queryTourTarget,
  useTourSpotlightRect,
  type SpotlightRect,
} from './useTourSpotlightRect';
import {
  Box,
  Button,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { createPortal } from 'react-dom';
import type { ReviewPathTourStep } from '../../../lib/employerReview/buildWhatsHappeningReviewPathSteps';
import {
  GuideBulletList,
  GuideIntroText,
  GuideStepProgress,
  GuideTipBlock,
} from './GuideTourPrimitives';
import { scrollBehaviorReducedAware } from '../../../lib/prefersReducedMotion';
import { guidePanelShellSx, guideTourTokens } from './guidedTourTheme';

export type SpotlightTourStep = Pick<
  ReviewPathTourStep,
  'id' | 'target' | 'title' | 'description' | 'points' | 'tip' | 'tipPoints'
>;

const TOUR_Z = 1400;
const VARIANT = 'review-path' as const;

function waitForTourTarget(target: string, maxMs = 900): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const el = queryTourTarget(target);
      if (el && el.getBoundingClientRect().height > 8) {
        resolve(el);
        return;
      }
      if (Date.now() - started >= maxMs) {
        resolve(el);
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

function popoverPosition(
  spotlight: SpotlightRect | null,
  useBottomSheet: boolean,
): Record<string, string | number> {
  if (useBottomSheet) {
    return {
      position: 'fixed',
      left: 12,
      right: 12,
      bottom: 12,
      maxHeight: '52vh',
      overflow: 'auto',
    };
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(400, vw - 24);

  if (!spotlight) {
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width,
    };
  }

  const preferBelow = spotlight.top + spotlight.height + 280 < vh;
  const preferRight = spotlight.left + spotlight.width + width + 24 < vw;

  if (preferBelow) {
    return {
      position: 'fixed',
      top: spotlight.top + spotlight.height + 14,
      left: Math.min(Math.max(12, spotlight.left), vw - width - 12),
      width,
    };
  }
  if (preferRight) {
    return {
      position: 'fixed',
      top: Math.min(Math.max(12, spotlight.top), vh - 300),
      left: spotlight.left + spotlight.width + 14,
      width,
    };
  }
  return {
    position: 'fixed',
    top: Math.max(12, spotlight.top - 280),
    left: Math.min(Math.max(12, spotlight.left), vw - width - 12),
    width,
  };
}

export function SpotlightTourOverlay({
  steps,
  stepIndex,
  ariaLabelId,
  onStepEnter,
  onBack,
  onNext,
  onSkip,
}: {
  steps: SpotlightTourStep[];
  stepIndex: number;
  ariaLabelId: string;
  /** Panel expand / focus side effects — run before scroll + spotlight measure. */
  onStepEnter?: (stepIndex: number) => void;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const theme = useTheme();
  const tokens = guideTourTokens(VARIANT, theme);
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const { scrollToAndLock } = usePageScrollLock(true, dialogRef as RefObject<HTMLElement | null>);

  const step = steps[stepIndex];
  const isLast = stepIndex >= steps.length - 1;
  const spotlight = useTourSpotlightRect(step?.target, Boolean(step));

  useLayoutEffect(() => {
    if (!step) return;

    let cancelled = false;
    let scrollTimer = 0;

    onStepEnter?.(stepIndex);

    void (async () => {
      const el = await waitForTourTarget(step.target);
      if (cancelled) return;
      scrollTimer = scrollToAndLock(el, () => {}, scrollBehaviorReducedAware());
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(scrollTimer);
    };
  }, [step?.target, stepIndex, scrollToAndLock, onStepEnter]);

  useEffect(() => {
    nextButtonRef.current?.focus({ preventScroll: true });
  }, [stepIndex]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onSkip();
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        onNext();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        onBack();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onBack, onNext, onSkip]);

  if (!step) return null;

  return createPortal(
    <>
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: TOUR_Z,
          bgcolor: alpha(theme.palette.common.black, 0.38),
          pointerEvents: 'auto',
        }}
        onClick={onSkip}
      />
      {spotlight ? (
        <Box
          aria-hidden
          sx={{
            position: 'fixed',
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
            borderRadius: 2,
            zIndex: TOUR_Z + 1,
            pointerEvents: 'none',
            outline: `2px solid ${tokens.spotlightRing}`,
            outlineOffset: 2,
            boxShadow: `0 0 0 4px ${tokens.spotlightGlow}`,
            transition: 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease',
          }}
        />
      ) : null}
      <Box
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelId}
        tabIndex={-1}
        sx={{
          ...popoverPosition(spotlight, isSmall),
          ...guidePanelShellSx(tokens, theme),
          zIndex: TOUR_Z + 2,
          pointerEvents: 'auto',
          p: 1.75,
          '@media (prefers-reduced-transparency: reduce)': {
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <GuideStepProgress stepIndex={stepIndex} totalSteps={steps.length} tokens={tokens} />
        <Typography id={ariaLabelId} variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5, lineHeight: 1.3 }}>
          {step.title}
        </Typography>
        <GuideIntroText>{step.description}</GuideIntroText>
        <Box sx={{ mb: step.tip || step.tipPoints?.length ? 1.1 : 1.35 }}>
          <GuideBulletList items={step.points} dotColor={tokens.accent} />
        </Box>
        {(step.tip || step.tipPoints?.length) ? (
          <Box sx={{ mb: 1.1 }}>
            <GuideTipBlock text={step.tip} points={step.tipPoints} tokens={tokens} />
          </Box>
        ) : null}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={onSkip}
            sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem' }}
          >
            Skip tour
          </Link>
          <Stack direction="row" spacing={0.85}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={onBack}
              disabled={stepIndex === 0}
              sx={{
                minWidth: 72,
                fontWeight: 700,
                borderRadius: 2,
                borderColor: tokens.accentBorder,
                color: 'text.secondary',
              }}
            >
              Back
            </Button>
            <Button
              ref={nextButtonRef}
              size="small"
              variant="contained"
              onClick={onNext}
              sx={{
                minWidth: 88,
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: tokens.accent,
                '&:hover': { bgcolor: alpha(tokens.accent, 0.88) },
              }}
            >
              {isLast ? 'Done' : 'Next →'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </>,
    document.body,
  );
}
